import type { ReactNode } from "react";

import { cn } from "../../lib/cn";

export interface QuotaMeterProps {
  label: ReactNode;
  used: number;
  limit: number;
  /** Unité affichée après les valeurs (« Gio », « instances », « req/mois »). */
  unit?: string;
  /** Mise en forme des nombres. Par défaut : séparateur de milliers français. */
  format?: (value: number) => string;
  /**
   * Seuil d'alerte souple, en pourcentage. Au-delà, la jauge passe en
   * avertissement. 75 % par défaut.
   */
  warnAt?: number;
  /** Seuil critique, en pourcentage. 90 % par défaut. */
  dangerAt?: number;
  /** Action proposée quand le quota se remplit (« Augmenter la limite »). */
  action?: ReactNode;
  className?: string;
}

const defaultFormat = (value: number) => value.toLocaleString("fr-FR");

/**
 * Jauge de quota.
 *
 * ── Ce qu'une barre de progression ordinaire ne sait pas dire ────────────────
 * Un quota n'est pas un chargement. Un chargement va vers 100 % et c'est une
 * bonne nouvelle ; un quota qui va vers 100 % est une panne annoncée — les
 * écritures échoueront, l'instance refusera de démarrer, la facture basculera.
 * Les deux ne peuvent donc pas se peindre pareil.
 *
 * Trois conséquences concrètes :
 *
 *   • La couleur suit le REMPLISSAGE, pas la marque. Sous le seuil d'alerte la
 *     jauge est neutre — un quota à 12 % ne mérite aucune couleur de marque,
 *     qui ne ferait qu'ajouter du bruit à une page qui en compte dix.
 *
 *   • Le reste disponible est écrit en toutes lettres. « 41 Gio restants » est
 *     ce que l'opérateur cherche ; « 82 % » l'oblige à faire le calcul.
 *
 *   • Un dépassement est représenté tel quel, pas écrêté à 100 %. Les quotas
 *     souples se dépassent réellement, et masquer le dépassement supprime
 *     précisément l'information qui déclenche l'action.
 */
export function QuotaMeter({
  label,
  used,
  limit,
  unit,
  format = defaultFormat,
  warnAt = 75,
  dangerAt = 90,
  action,
  className,
}: QuotaMeterProps) {
  const ratio = limit > 0 ? (used / limit) * 100 : 0;
  const isOver = ratio > 100;
  const percent = Math.min(ratio, 100);
  const remaining = Math.max(limit - used, 0);

  const tone =
    isOver || ratio >= dangerAt ? "danger" : ratio >= warnAt ? "warning" : "neutral";

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="text-sm font-medium text-fg">{label}</span>
        <span className="text-sm text-fg-muted tabular-nums">
          <span
            className={cn(
              "font-semibold",
              tone === "danger" && "text-danger-fg",
              tone === "warning" && "text-warning-fg",
              tone === "neutral" && "text-fg",
            )}
          >
            {format(used)}
          </span>
          {" / "}
          {format(limit)}
          {unit && ` ${unit}`}
        </span>
      </div>

      <div
        role="meter"
        aria-valuenow={Math.round(ratio)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={typeof label === "string" ? label : undefined}
        className="relative h-2 overflow-hidden rounded-full bg-surface-sunken inset-shadow-control"
      >
        <div
          className={cn(
            "h-full rounded-full transition-[width] duration-slow ease-standard",
            tone === "danger" && "bg-danger",
            tone === "warning" && "bg-warning",
            tone === "neutral" && "bg-accent",
          )}
          style={{ width: `${percent}%` }}
        />

        {/* Repère du seuil d'alerte : il donne à la barre une graduation, donc
            un « où j'en suis » plutôt qu'un simple « combien ». */}
        {!isOver && warnAt > 0 && warnAt < 100 && (
          <span
            className="absolute inset-y-0 w-px bg-border-strong/70"
            style={{ left: `${warnAt}%` }}
            aria-hidden="true"
          />
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <span
          className={cn(
            "text-xs tabular-nums",
            isOver ? "font-medium text-danger-fg" : "text-fg-muted",
          )}
        >
          {isOver
            ? `Dépassement de ${format(used - limit)}${unit ? ` ${unit}` : ""} (${Math.round(ratio)} %)`
            : `${format(remaining)}${unit ? ` ${unit}` : ""} restants — ${Math.round(ratio)} % utilisés`}
        </span>
        {action}
      </div>
    </div>
  );
}
